import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  api,
  ApiError,
  CHANNELS,
  FILMED_CHANNELS,
  reelSource,
  type Channel,
  type FilmedChannel,
  type Idea,
  type IdeaEvent,
  type Status,
} from '../api'
import { goBack, navigate } from '../app/routing'
import { ChannelIcon } from '../components/ChannelIcon'
import {
  BackIcon,
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  CopyIcon,
  KillIcon,
  LinkIcon,
  MoreIcon,
  PanelCloseIcon,
  PanelOpenIcon,
  TrashIcon,
} from '../components/icons'
import { Markdown } from '../components/Markdown'
import { FORMAT_OPTIONS, SERIES_OPTIONS, STATUS_OPTIONS, changeStatus, setPosted } from '../components/pickers'
import { PostedMark } from '../components/PostedMark'
import { SeriesDot } from '../components/SeriesDot'
import { PickerSheet, Sheet } from '../components/Sheet'
import { StatusIcon } from '../components/StatusIcon'
import { POST_TEXT, POST_TEXT_FIELDS, isFilmable, isFilmedChannel, orderChannels, type PostTextField } from '../domain/formats'
import { CHANNEL_LABEL, FORMAT_LABEL, SERIES_LABEL, STATUS_LABEL } from '../domain/labels'
import { STAGES, STEP_LABEL, stageOf } from '../domain/stages'
import { relativeTime } from '../domain/time'
import { deleteIdea, isPending, savingStore, updateIdea, upsertIdea, useIdeas } from '../state/ideas'
import { moveToStage } from '../state/moves'
import { panelStore, usePanel } from '../state/panel'
import { useQueue } from '../state/queue'
import { stageViewStore } from '../state/stageView'
import { useStore } from '../state/store'
import { toast, toastError } from '../state/toast'
import { ScriptEditor } from './ScriptEditor'
import './idea-detail.css'

type Picker = 'status' | 'series' | 'format' | 'channels' | 'more' | null

/**
 * One idea, edited where it stands.
 *
 * The same editor twice: a page of its own on the phone, a panel beside the
 * canvas on a laptop. At the top, where the idea is in the pipeline, as a row
 * of steps that also moves it. Then the title, one quiet line of properties
 * (series, format, channels) that each open a picker, and the script, beat by
 * beat. Notes are what is not read out loud; the activity log folds into one
 * line at the bottom. The idea comes from the shared list, so it paints at
 * once; the fetch that follows brings the activity log.
 */
function useIdeaWithEvents(id: number) {
  const { ideas, phase } = useIdeas()
  const idea = ideas.find((item) => item.id === id)
  const [events, setEvents] = useState<IdeaEvent[] | null>(null)
  const [missing, setMissing] = useState(false)

  const updatedAt = idea?.updated_at
  useEffect(() => {
    let current = true
    api
      .getIdea(id)
      .then(({ events: log, ...fresh }) => {
        if (!current) return
        setEvents(log)
        upsertIdea(fresh)
      })
      .catch((error) => {
        if (current && error instanceof ApiError && error.status === 404) setMissing(true)
      })
    return () => {
      current = false
    }
  }, [id, updatedAt])

  return { idea, events, gone: !idea && (missing || phase === 'ready') }
}

/** On the phone: a page, with Back. */
export function IdeaDetail({ id }: { id: number }) {
  const { idea, events, gone } = useIdeaWithEvents(id)
  const { ids } = useQueue()
  const [picker, setPicker] = useState<Picker>(null)

  // Back on the list, land on the stage the idea is in now, with its row lit.
  const where = idea ? stageOf(idea, ids) : null
  useEffect(
    () => () => {
      if (where) stageViewStore.set({ stage: where, flash: id })
    },
    [where, id],
  )

  return (
    <main className="screen detail">
      <PageBar idea={idea} onMore={idea ? () => setPicker('more') : undefined} />
      {idea ? (
        <article className="detail-page">
          <Editor idea={idea} events={events} onPick={setPicker} />
        </article>
      ) : (
        <p className="empty">{gone ? 'This idea is gone.' : ''}</p>
      )}
      {idea && <Sheets idea={idea} picker={picker} onPick={setPicker} />}
    </main>
  )
}

/** On a laptop: a panel beside the canvas, folded to a strip or open. */
export function IdeaPanel({ id }: { id: number }) {
  const { idea, events, gone } = useIdeaWithEvents(id)
  const state = usePanel()
  const [picker, setPicker] = useState<Picker>(null)
  const close = () => navigate('/')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target as HTMLElement | null
      if (target?.closest('textarea, input, [contenteditable="true"]')) return
      if (document.querySelector('.sheet-layer[data-open="true"]')) return
      navigate('/')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (state === 'collapsed') {
    return (
      <aside className="idea-panel" data-state="collapsed" aria-label="Idea">
        <button type="button" className="idea-panel-strip" onClick={() => panelStore.set('open')}>
          <PanelOpenIcon size={19} />
          <span className="detail-id mono">#{id}</span>
          <span className="idea-panel-strip-title">{idea?.title}</span>
        </button>
      </aside>
    )
  }

  return (
    <aside className="idea-panel" data-state="open" aria-label={idea ? `Idea #${id}` : 'Idea'}>
      <header className="idea-panel-bar">
        <span className="detail-id mono">#{id}</span>
        {idea && <SaveState id={idea.id} />}
        <span className="idea-panel-grow" />
        {idea && (
          <button type="button" className="icon-button" aria-label="More" onClick={() => setPicker('more')}>
            <MoreIcon size={20} />
          </button>
        )}
        <button type="button" className="icon-button" aria-label="Fold the panel away" onClick={() => panelStore.set('collapsed')}>
          <PanelCloseIcon size={19} />
        </button>
        <button type="button" className="icon-button" aria-label="Close" title="Close (Esc)" onClick={close}>
          <CloseIcon size={20} />
        </button>
      </header>
      <div className="idea-panel-scroll">
        {idea ? (
          <article className="detail-page">
            <Editor idea={idea} events={events} onPick={setPicker} wide />
          </article>
        ) : (
          <p className="empty">{gone ? 'This idea is gone.' : ''}</p>
        )}
      </div>
      {idea && <Sheets idea={idea} picker={picker} onPick={setPicker} />}
    </aside>
  )
}

function Editor({
  idea,
  events,
  onPick,
  wide = false,
}: {
  idea: Idea
  events: IdeaEvent[] | null
  onPick: (picker: Picker) => void
  wide?: boolean
}) {
  const filmable = isFilmable(idea)
  const postable = idea.status === 'edited' || idea.status === 'published' || idea.channels.includes('linkedin')
  return (
    <>
      <StageSteps idea={idea} />
      <div className="detail-top">
        <div className="detail-head">
          <TitleField idea={idea} />
          <PropertyLine idea={idea} onPick={onPick} />
          {postable && <PostedOn idea={idea} />}
        </div>
        {wide && filmable && <CardPreview idea={idea} />}
      </div>
      <Videos idea={idea} />
      {filmable && <ScriptEditor key={idea.id} idea={idea} />}
      <section className="detail-section" aria-label="Notes">
        <div className="detail-section-head">
          <h2 className="detail-section-title">Notes</h2>
          <span className="detail-section-hint">{filmable ? 'Research, links, the angle. Not read out loud.' : 'What you write the post from.'}</span>
        </div>
        <BodyField idea={idea} />
      </section>
      {(idea.status === 'edited' || idea.status === 'published' || idea.published_url) && <PublishedLink idea={idea} />}
      <ActivitySummary idea={idea} events={events} />
      <IdeaActions idea={idea} onStatus={() => onPick('status')} />
    </>
  )
}

/**
 * Where the idea is, as the steps it goes through: done, here, still ahead.
 * A step it can move to is a button. To post is only reached by filming, and
 * a filmed idea does not go back.
 */
function StageSteps({ idea }: { idea: Idea }) {
  const { ids } = useQueue()
  if (idea.status === 'killed') {
    return (
      <p className="detail-state">
        Killed.
        <button type="button" className="text-button" onClick={() => changeStatus(idea, 'idea')}>
          Bring it back
        </button>
      </p>
    )
  }
  if (!isFilmable(idea)) return <p className="detail-state">A LinkedIn topic: written, not filmed.</p>

  const stage = stageOf(idea, ids)
  const current = stage ? STAGES.indexOf(stage) : STAGES.length
  const filmed = stage === 'post' || stage === null
  const note =
    stage === 'filming'
      ? `Number ${ids.indexOf(idea.id) + 1} of ${ids.length} in the next batch.`
      : idea.status === 'filmed'
        ? 'Filmed. The video is on its way from the edit.'
        : idea.status === 'edited'
          ? 'Back from the edit. Tick where it is posted.'
          : idea.status === 'published'
            ? 'Posted everywhere it is going.'
            : null

  return (
    <div className="detail-steps">
      <nav className="steps" aria-label="Stage">
        {STAGES.map((step, index) => {
          const locked = step === 'post' ? true : filmed
          const state = index < current ? 'done' : index === current ? 'current' : 'ahead'
          return (
            <Fragment key={step}>
              {index > 0 && <span className="step-line" aria-hidden="true" />}
              <button
                type="button"
                className="step"
                data-state={state}
                aria-current={state === 'current' ? 'step' : undefined}
                disabled={locked || state === 'current' || isPending(idea)}
                title={
                  step === 'post' && state !== 'current'
                    ? 'Videos arrive here from the edit'
                    : locked && state !== 'current'
                      ? 'Filmed already'
                      : undefined
                }
                onClick={() => moveToStage(idea, step)}
              >
                <span className="step-mark">{state === 'done' && <CheckIcon size={11} strokeWidth={2.6} />}</span>
                {STEP_LABEL[step]}
              </button>
            </Fragment>
          )
        })}
      </nav>
      {note && <p className="step-note">{note}</p>}
    </div>
  )
}

/**
 * The title as it will stand on screen: the card the pipeline cuts to, in
 * the same face and the same series rule, shrunk to fit the way the film
 * screen fits it.
 */
function CardPreview({ idea }: { idea: Idea }) {
  const longest = Math.max(1, ...idea.title.split(/\s+/).map((word) => word.length))
  const length = idea.title.length
  const byLength = length <= 14 ? 18 : length <= 32 ? 14.5 : length <= 60 ? 12 : 10
  const size = Math.max(9, Math.min(byLength, 150 / longest))
  return (
    <figure className="card-preview" data-series={idea.series}>
      <div className="card-preview-frame">
        <span className="card-preview-rule" />
        <span className="card-preview-text" style={{ fontSize: size }}>
          {idea.title}
        </span>
      </div>
      <figcaption>On screen</figcaption>
    </figure>
  )
}

const capitalised = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/** Series, format and channels on one line; each opens its picker. */
function PropertyLine({ idea, onPick }: { idea: Idea; onPick: (picker: Picker) => void }) {
  const pending = isPending(idea)
  return (
    <div className="props" role="group" aria-label="Properties">
      <button type="button" className="prop" disabled={pending} onClick={() => onPick('series')}>
        <SeriesDot series={idea.series} />
        {SERIES_LABEL[idea.series]}
      </button>
      <span className="prop-sep" aria-hidden="true">
        ·
      </span>
      <button type="button" className="prop" disabled={pending} onClick={() => onPick('format')}>
        {capitalised(FORMAT_LABEL[idea.format])}
      </button>
      <span className="prop-sep" aria-hidden="true">
        ·
      </span>
      <button type="button" className="prop" disabled={pending} aria-label="Channels" onClick={() => onPick('channels')}>
        {idea.channels.map((channel) => CHANNEL_LABEL[channel]).join(' + ')}
      </button>
    </div>
  )
}

function Sheets({ idea, picker, onPick }: { idea: Idea; picker: Picker; onPick: (picker: Picker) => void }) {
  const onClose = () => onPick(null)
  return (
    <>
      <PickerSheet
        open={picker === 'status'}
        title="Status"
        options={STATUS_OPTIONS}
        value={idea.status}
        onPick={(status: Status) => changeStatus(idea, status)}
        onClose={onClose}
      />
      <PickerSheet
        open={picker === 'series'}
        title="Series"
        options={SERIES_OPTIONS}
        value={idea.series}
        onPick={(series) => void updateIdea(idea.id, { series })}
        onClose={onClose}
      />
      <PickerSheet
        open={picker === 'format'}
        title="Format"
        options={FORMAT_OPTIONS}
        value={idea.format}
        onPick={(format) => void updateIdea(idea.id, { format })}
        onClose={onClose}
      />
      <ChannelsSheet idea={idea} open={picker === 'channels'} onClose={onClose} />
      <MoreSheet idea={idea} open={picker === 'more'} onClose={onClose} onStatus={() => onPick('status')} />
    </>
  )
}

/**
 * Where the idea goes, as three toggles. At least one stays on: tapping the
 * last one says so instead of leaving an idea that goes nowhere.
 */
function ChannelsSheet({ idea, open, onClose }: { idea: Idea; open: boolean; onClose: () => void }) {
  const toggle = (channel: Channel) => {
    const on = idea.channels.includes(channel)
    if (on && idea.channels.length === 1) {
      toast(`Add another channel before dropping ${CHANNEL_LABEL[channel]}`)
      return
    }
    if (on && idea.posted_to.includes(channel)) {
      toast(`It is marked posted on ${CHANNEL_LABEL[channel]}. Unmark that first`)
      return
    }
    if (on && isFilmedChannel(channel) && idea.reels[channel]) {
      toast(`The ${CHANNEL_LABEL[channel]} video is stored. Delete it first`)
      return
    }
    const next = on ? idea.channels.filter((c) => c !== channel) : [...idea.channels, channel]
    void updateIdea(idea.id, { channels: orderChannels(next) })
  }
  return (
    <Sheet open={open} onClose={onClose} label="Channels">
      <div className="picker">
        <h2 className="picker-title">Channels</h2>
        <ul className="picker-list" aria-label="Channels">
          {CHANNELS.map((channel) => {
            const on = idea.channels.includes(channel)
            return (
              <li key={channel}>
                <button
                  type="button"
                  aria-pressed={on}
                  className="picker-option"
                  disabled={isPending(idea)}
                  onClick={() => toggle(channel)}
                >
                  <span className="picker-icon">
                    <ChannelIcon channel={channel} size={20} />
                  </span>
                  <span className="picker-label">{CHANNEL_LABEL[channel]}</span>
                  <span className="picker-check" aria-hidden="true">
                    {on && <CheckIcon size={18} />}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </Sheet>
  )
}

/**
 * Where the idea is out, one small toggle per channel. Posting is per
 * channel; status stays the video pipeline, and marking every video channel
 * posted is what moves it to Published. A video channel is offered once
 * there is a video to post; LinkedIn, which Martin writes, whenever it is one
 * of the idea's channels.
 */
function PostedOn({ idea }: { idea: Idea }) {
  const made = idea.status === 'edited' || idea.status === 'published'
  const channels = idea.channels.filter((channel) => channel === 'linkedin' || made || idea.posted_to.includes(channel))
  return (
    <div className="detail-posted" role="group" aria-label="Posted on">
      <span className="detail-posted-label">Posted on</span>
      {channels.map((channel) => {
        const posted = idea.posted_to.includes(channel)
        const label = CHANNEL_LABEL[channel]
        return (
          <button
            key={channel}
            type="button"
            className="posted-toggle"
            aria-pressed={posted}
            aria-label={posted ? `Posted on ${label}. Tap to undo` : `Mark posted on ${label}`}
            disabled={isPending(idea)}
            onClick={() => setPosted(idea, channel, !posted)}
          >
            <PostedMark posted={posted} size={15} />
            {label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * "Saving…" while a change is on its way, "Saved" for a moment after, and
 * "Not saved" when the server refused it (the toast says why).
 */
function SaveState({ id }: { id: number }) {
  const state = useStore(savingStore).get(id)
  const [justSaved, setJustSaved] = useState(false)
  const was = useRef(state)
  useEffect(() => {
    const landed = was.current === 'saving' && state === undefined
    was.current = state
    if (!landed) return
    setJustSaved(true)
    const timer = setTimeout(() => setJustSaved(false), 1800)
    return () => clearTimeout(timer)
  }, [state])
  const text = state === 'saving' ? 'Saving…' : state === 'failed' ? 'Not saved' : justSaved ? 'Saved' : null
  if (!text) return null
  return (
    <span className="save-state" data-failed={state === 'failed' || undefined} aria-live="polite">
      {text}
    </span>
  )
}

function PageBar({ idea, onMore }: { idea?: Idea; onMore?: () => void }) {
  const scrolled = useScrolled()
  return (
    <header className="topbar" data-scrolled={scrolled}>
      <button type="button" className="icon-button" aria-label="Back" onClick={() => goBack('/')}>
        <BackIcon />
      </button>
      <span className="topbar-title">
        {idea && (
          <>
            <span className="detail-id mono">#{idea.id}</span>
            <span className="detail-bar-title" data-visible={scrolled}>
              {idea.title}
            </span>
          </>
        )}
      </span>
      {idea && <SaveState id={idea.id} />}
      {onMore && (
        <button type="button" className="icon-button" aria-label="More" onClick={onMore}>
          <MoreIcon />
        </button>
      )}
    </header>
  )
}

function useScrolled(threshold = 24): boolean {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [threshold])
  return scrolled
}

function useAutosize(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }, [value])
  return ref
}

function TitleField({ idea }: { idea: Idea }) {
  const [draft, setDraft] = useState(idea.title)
  const [editing, setEditing] = useState(false)
  const ref = useAutosize(draft)

  // Follow the server (or an undo) while the field is not being typed in.
  useEffect(() => {
    if (!editing) setDraft(idea.title)
  }, [idea.title, editing])

  const commit = () => {
    setEditing(false)
    const title = draft.replace(/\s+/g, ' ').trim()
    if (!title) {
      setDraft(idea.title)
      return
    }
    if (title !== idea.title) void updateIdea(idea.id, { title })
  }

  return (
    <textarea
      ref={ref}
      className="detail-title"
      aria-label="Title"
      rows={1}
      value={draft}
      enterKeyHint="done"
      onFocus={() => setEditing(true)}
      onChange={(event) => setDraft(event.target.value.replace(/\n/g, ' '))}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          event.currentTarget.blur()
        }
        if (event.key === 'Escape') {
          setDraft(idea.title)
          event.currentTarget.blur()
        }
      }}
      onBlur={commit}
    />
  )
}

function BodyField({ idea }: { idea: Idea }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(idea.body)
  const ref = useAutosize(editing ? draft : '')

  useEffect(() => {
    if (!editing) setDraft(idea.body)
  }, [idea.body, editing])

  useEffect(() => {
    if (editing) ref.current?.focus({ preventScroll: true })
  }, [editing, ref])

  const commit = () => {
    setEditing(false)
    if (draft !== idea.body) void updateIdea(idea.id, { body: draft.trimEnd() })
  }

  if (editing) {
    return (
      <div className="detail-notes editing">
        <textarea
          ref={ref}
          className="detail-notes-field"
          aria-label="Notes"
          value={draft}
          placeholder="Markdown works."
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) event.currentTarget.blur()
            if (event.key === 'Escape') event.currentTarget.blur()
          }}
          onBlur={commit}
        />
        <div className="detail-notes-actions">
          <span className="mono">Markdown</span>
          <button type="button" className="button button-primary detail-done" onMouseDown={(e) => e.preventDefault()} onClick={() => ref.current?.blur()}>
            Done
          </button>
        </div>
      </div>
    )
  }

  // A div rather than a <button>: the notes hold headings, lists and links,
  // none of which may sit inside a button. It still takes focus and Enter.
  return (
    <div
      role="button"
      tabIndex={0}
      className="detail-notes"
      data-empty={!idea.body || undefined}
      aria-label={idea.body ? 'Edit notes' : 'Add notes'}
      onClick={(event) => {
        // A tap on a link inside the notes opens the link, not the editor.
        if ((event.target as HTMLElement).closest('a')) return
        setEditing(true)
      }}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          setEditing(true)
        }
      }}
    >
      {idea.body ? <Markdown source={idea.body} /> : <span className="detail-notes-placeholder">Add notes</span>}
    </div>
  )
}

/**
 * What has been filmed, one player per channel that has a video, and under each
 * what that video carries when it goes out, to copy while uploading.
 *
 * The files arrive from the pipeline, which uploads a reel for Shorts and a
 * long-form cut for YouTube, so an idea can have one, both or neither. The
 * Short keeps a phone-sized 9:16 frame; YouTube takes the full width. Text
 * written before its video arrives is shown on its own.
 */
export function Videos({ idea }: { idea: Idea }) {
  const showing = FILMED_CHANNELS.filter(
    (channel) => idea.reels[channel] || fieldsFor(channel).some((field) => idea[field]),
  )
  if (!showing.length) return null
  return (
    <section className="detail-section detail-reels" aria-label="Videos">
      <h2 className="detail-section-title">Videos</h2>
      {showing.map((channel) => (
        <Video key={channel} idea={idea} channel={channel} />
      ))}
    </section>
  )
}

function Video({ idea, channel }: { idea: Idea; channel: FilmedChannel }) {
  const src = reelSource(idea, channel)
  return (
    <figure className="reel-figure">
      <figcaption className="reel-label">
        <ChannelIcon channel={channel} size={14} />
        {CHANNEL_LABEL[channel]}
      </figcaption>
      {src && <video className="reel" data-channel={channel} src={src} controls playsInline preload="metadata" />}
      {fieldsFor(channel).map((field) => (
        <PostText key={field} idea={idea} field={field} />
      ))}
    </figure>
  )
}

const fieldsFor = (channel: FilmedChannel) => POST_TEXT_FIELDS.filter((field) => POST_TEXT[field].channel === channel)

const POST_TEXT_LABEL: Record<PostTextField, string> = {
  short_caption: 'Caption',
  youtube_title: 'Title',
  youtube_description: 'Description',
}

/**
 * One piece of text the video goes out with, shown as it will be pasted and
 * copied with one tap. A tap on the text edits it, like the notes, with the
 * count against the platform's limit while typing. Text over the limit is not
 * saved: the field stays open and says so.
 */
function PostText({ idea, field }: { idea: Idea; field: PostTextField }) {
  const { limit } = POST_TEXT[field]
  const label = POST_TEXT_LABEL[field]
  const name = label.toLowerCase()
  const oneLine = field === 'youtube_title'
  const saved = idea[field]
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(saved)
  const ref = useAutosize(editing ? draft : '')

  useEffect(() => {
    if (!editing) setDraft(saved)
  }, [saved, editing])

  useEffect(() => {
    if (editing) ref.current?.focus({ preventScroll: true })
  }, [editing, ref])

  const length = draft.trim().length
  const over = length > limit

  const commit = () => {
    if (over) {
      toast(`The ${name} is ${length.toLocaleString('en')} characters, and the most is ${limit.toLocaleString('en')}`, {
        tone: 'error',
      })
      return
    }
    setEditing(false)
    if (draft.trim() !== saved) void updateIdea(idea.id, { [field]: draft.trim() })
  }

  const copy = () =>
    navigator.clipboard.writeText(saved).then(
      () => toast(`${label} copied`),
      (error) => toastError(error, `Could not copy the ${name}`),
    )

  return (
    <div className="post-text">
      <div className="post-text-head">
        <span className="post-text-label">{label}</span>
        {!editing && saved && (
          <button type="button" className="post-text-copy" onClick={() => void copy()} aria-label={`Copy the ${name}`}>
            <CopyIcon size={15} />
            Copy
          </button>
        )}
      </div>
      {editing ? (
        <div className="post-text-editing">
          <textarea
            ref={ref}
            className="post-text-field"
            aria-label={label}
            rows={1}
            value={draft}
            enterKeyHint={oneLine ? 'done' : undefined}
            onChange={(event) => setDraft(oneLine ? event.target.value.replace(/\n/g, ' ') : event.target.value)}
            onKeyDown={(event) => {
              const done = oneLine ? event.key === 'Enter' : event.key === 'Enter' && (event.metaKey || event.ctrlKey)
              if (done || event.key === 'Escape') {
                event.preventDefault()
                event.currentTarget.blur()
              }
            }}
            onBlur={commit}
          />
          <div className="detail-notes-actions">
            <span className="mono post-text-count" data-over={over || undefined}>
              {length.toLocaleString('en')} / {limit.toLocaleString('en')}
            </span>
            <button
              type="button"
              className="button button-primary detail-done"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => ref.current?.blur()}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="post-text-body"
          data-empty={!saved || undefined}
          data-one-line={oneLine || undefined}
          aria-label={saved ? `Edit the ${name}` : `Add the ${name}`}
          onClick={() => setEditing(true)}
        >
          {saved || <span className="post-text-placeholder">Add the {name}</span>}
        </button>
      )}
    </div>
  )
}

function PublishedLink({ idea }: { idea: Idea }) {
  const [draft, setDraft] = useState(idea.published_url ?? '')
  useEffect(() => setDraft(idea.published_url ?? ''), [idea.published_url])
  const valid = /^https?:\/\/\S+$/.test(draft.trim())
  return (
    <section className="detail-section">
      <h2 className="detail-section-title">Published</h2>
      <div className="published-row">
        <input
          className="published-field"
          type="url"
          inputMode="url"
          placeholder="Paste the post's link"
          aria-label="Published link"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const next = draft.trim() || null
            if (next !== idea.published_url) void updateIdea(idea.id, { published_url: next })
          }}
        />
        {valid && (
          <a className="icon-button" href={draft.trim()} target="_blank" rel="noopener noreferrer" aria-label="Open post">
            <LinkIcon size={20} />
          </a>
        )}
      </div>
    </section>
  )
}

const SOURCE_LINE: Record<string, string> = {
  phone: 'Added from the phone',
  claude: 'Added by Claude',
  bot: 'Added by the bot',
  comment: 'Added from a comment',
  research: 'Added from research',
  import: 'Imported',
}

const EDITED_LINE: Record<string, string> = {
  title: 'Edited the title',
  body: 'Edited the notes',
  script: 'Edited the script',
  series: 'Changed the series',
  format: 'Changed the format',
  short_caption: 'Edited the caption',
  youtube_title: 'Edited the YouTube title',
  youtube_description: 'Edited the YouTube description',
}

export function describeEvent(event: IdeaEvent): { text: ReactNode; status?: Status } {
  switch (event.kind) {
    case 'created':
      return { text: SOURCE_LINE[event.detail] ?? 'Created' }
    case 'status': {
      const [from, to] = event.detail.split(/\s*(?:→|->)\s*/)
      const target = (to ?? from) as Status
      const known = target in STATUS_LABEL
      return {
        status: known ? target : undefined,
        text: known ? (
          <>
            Moved to <strong>{STATUS_LABEL[target]}</strong>
          </>
        ) : (
          `Status: ${event.detail}`
        ),
      }
    }
    case 'edited':
      return { text: EDITED_LINE[event.detail] ?? 'Edited' }
    case 'batched':
      return {
        text: event.detail ? (
          <>
            Added to <strong>{event.detail}</strong>
          </>
        ) : (
          'Added to a filming batch'
        ),
      }
    case 'reel':
    case 'reel_removed': {
      const channel = event.detail as Channel
      const name = <strong>{CHANNEL_LABEL[channel] ?? event.detail}</strong>
      return event.kind === 'reel'
        ? { status: 'edited', text: <>Video uploaded for {name}</> }
        : { text: <>Video removed for {name}</> }
    }
    case 'channels':
      return { text: describeChannels(event.detail) }
    case 'posted':
    case 'unposted': {
      const channel = event.detail as Channel
      const name = <strong>{CHANNEL_LABEL[channel] ?? event.detail}</strong>
      return event.kind === 'posted'
        ? { status: 'published', text: <>Posted on {name}</> }
        : { text: <>Unmarked as posted on {name}</> }
    }
    default:
      return { text: event.detail || 'Changed' }
  }
}

/** `short → short, linkedin` reads as "Added LinkedIn". */
function describeChannels(detail: string): ReactNode {
  const [before, after] = detail.split(/\s*(?:→|->)\s*/).map((side) =>
    side.split(',').map((part) => part.trim()).filter((part): part is Channel => part in CHANNEL_LABEL),
  )
  if (!after) return `Channels: ${detail}`
  const added = after.filter((channel) => !before.includes(channel))
  const removed = before.filter((channel) => !after.includes(channel))
  const names = (channels: Channel[]) =>
    channels.map((channel, index) => (
      <Fragment key={channel}>
        {index > 0 && ' and '}
        <strong>{CHANNEL_LABEL[channel]}</strong>
      </Fragment>
    ))
  return (
    <>
      {added.length > 0 && <>Added {names(added)}</>}
      {added.length > 0 && removed.length > 0 && ', '}
      {removed.length > 0 && <>{added.length ? 'dropped' : 'Dropped'} {names(removed)}</>}
      {!added.length && !removed.length && 'Changed the channels'}
    </>
  )
}

/**
 * The activity log, folded to its first line: where the idea came from and
 * how much has happened since. Open, it is the whole log.
 */
function ActivitySummary({ idea, events }: { idea: Idea; events: IdeaEvent[] | null }) {
  const [open, setOpen] = useState(false)
  if (events === null) {
    return isPending(idea) ? null : <p className="activity-loading">Loading the activity…</p>
  }
  if (!events.length) return null
  const since = events.length - 1
  return (
    <section className="detail-section activity" aria-label="Activity">
      <button type="button" className="activity-summary" aria-expanded={open} onClick={() => setOpen((shown) => !shown)}>
        <span>
          {describeEvent(events[0]).text}
          {since > 0 && `, ${since} ${since === 1 ? 'change' : 'changes'} since`}
        </span>
        <ChevronIcon size={16} className="activity-chevron" />
      </button>
      {open && (
        <ol className="activity-list">
          {events.map((event, index) => {
            const { text, status } = describeEvent(event)
            return (
              <li key={`${event.at}-${index}`} className="activity-item">
                <span className="activity-mark">
                  {status ? <StatusIcon status={status} size={14} /> : <span className="activity-dot" />}
                </span>
                <span className="activity-text">{text}</span>
                <time className="activity-time mono" dateTime={event.at} title={new Date(event.at).toLocaleString('en-GB')}>
                  {relativeTime(event.at)}
                </time>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}

/**
 * What can be done to the idea as a whole: copy its link, set its status by
 * hand, kill it or bring it back, delete it. The same four in the more menu
 * and at the end of the page, so neither drifts from the other. Delete asks
 * once more before it acts, and forgets the question after a few seconds.
 */
function useIdeaActions(idea: Idea, done: () => void = () => {}) {
  const [confirming, setConfirming] = useState(false)
  useEffect(() => {
    if (!confirming) return
    const timer = setTimeout(() => setConfirming(false), 4000)
    return () => clearTimeout(timer)
  }, [confirming])

  const reset = useCallback(() => setConfirming(false), [])
  const killed = idea.status === 'killed'
  return {
    killed,
    confirming,
    reset,
    copyLink: async () => {
      const url = `${window.location.origin}/idea/${idea.id}`
      try {
        await navigator.clipboard.writeText(url)
        toast('Link copied')
      } catch {
        toast(url)
      }
      done()
    },
    toggleKill: () => {
      changeStatus(idea, killed ? 'idea' : 'killed')
      done()
    },
    remove: async () => {
      if (!confirming) {
        setConfirming(true)
        return
      }
      // Leave first: the list drops the row at once, and a page for an idea
      // that no longer exists has nothing to show meanwhile.
      done()
      navigate('/', { replace: true })
      if (await deleteIdea(idea.id)) toast(`Deleted #${idea.id}`)
    },
  }
}

/** The idea's actions again, as a row of buttons at the end of the page. */
function IdeaActions({ idea, onStatus }: { idea: Idea; onStatus: () => void }) {
  const actions = useIdeaActions(idea)
  const pending = isPending(idea)
  return (
    <section className="detail-actions" aria-label="Actions">
      <button type="button" className="detail-action" disabled={pending} onClick={() => void actions.copyLink()}>
        <CopyIcon size={17} />
        Copy link
      </button>
      <button type="button" className="detail-action" disabled={pending} onClick={onStatus}>
        <StatusIcon status={idea.status} size={15} />
        Set the status by hand
      </button>
      <button type="button" className="detail-action" disabled={pending} onClick={actions.toggleKill}>
        <KillIcon size={17} />
        {actions.killed ? 'Bring back as an idea' : 'Kill idea'}
      </button>
      <button
        type="button"
        className="detail-action"
        data-danger
        data-confirming={actions.confirming || undefined}
        disabled={pending}
        onClick={() => void actions.remove()}
      >
        <TrashIcon size={17} />
        {actions.confirming ? 'Delete for good' : 'Delete'}
      </button>
    </section>
  )
}

function MoreSheet({
  idea,
  open,
  onClose,
  onStatus,
}: {
  idea: Idea
  open: boolean
  onClose: () => void
  onStatus: () => void
}) {
  const actions = useIdeaActions(idea, onClose)
  const { reset } = actions
  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  return (
    <Sheet open={open} onClose={onClose} label="More">
      <div className="picker">
        <h2 className="picker-title mono">#{idea.id}</h2>
        <ul className="picker-list">
          <li>
            <button type="button" className="picker-option" onClick={() => void actions.copyLink()}>
              <span className="picker-icon">
                <CopyIcon size={20} />
              </span>
              <span className="picker-label">Copy link</span>
            </button>
          </li>
          <li>
            <button type="button" className="picker-option" onClick={onStatus}>
              <span className="picker-icon">
                <StatusIcon status={idea.status} size={18} />
              </span>
              <span className="picker-label">Set the status by hand</span>
              <span className="picker-hint mono">{STATUS_LABEL[idea.status]}</span>
            </button>
          </li>
          <li>
            <button type="button" className="picker-option" onClick={actions.toggleKill}>
              <span className="picker-icon">
                <KillIcon size={20} />
              </span>
              <span className="picker-label">{actions.killed ? 'Bring back as an idea' : 'Kill idea'}</span>
            </button>
          </li>
          <li>
            <button type="button" className="picker-option danger" onClick={() => void actions.remove()}>
              <span className="picker-icon">
                <TrashIcon size={20} />
              </span>
              <span className="picker-label">{actions.confirming ? 'Delete for good' : 'Delete'}</span>
            </button>
          </li>
        </ul>
      </div>
    </Sheet>
  )
}
